import React, { useMemo, useState } from 'react';
import { LuSparkles, LuChevronLeft, LuBan, LuListOrdered, LuQuote, LuInfo, LuPalette, LuUser } from 'react-icons/lu';
import NodeEditorChrome from '../Components/NodeEditorChrome';
import { SegmentedTabs, useNodeForm } from '../Components/NodeFormKit';
import { NODE_THEMES } from '../Components/NodeTheme';
import { AGENT_COLOR, MODELS, TOOL_TYPES } from './agentCatalog';
import { AddLink, Field, FieldPicker, FormSection, InlineSwitch, PairListEditor, RemoveButton, SelectInput, StringListEditor, TextArea, TextInput, ToggleField, csvToList } from './AgentFields';
import { ConfigSection } from '../Others/BotConfigKit';
import ToolEditor from './ToolEditor';

export const ValidationBox = ({ validation }) => {
  const { theme } = useNodeForm();
  if (!validation || (!validation.errors?.length && !validation.warnings?.length)) return null;
  const row = (text, color) => (
    <div key={text} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', lineHeight: 1.45, padding: '8px 10px', borderRadius: '10px', backgroundColor: theme.inputBg, border: `1px solid ${theme.border}`, color: theme.textPrimary }}>
      <span style={{ width: '7px', height: '7px', minWidth: '7px', borderRadius: '50%', backgroundColor: color, marginTop: '5px' }} />
      {text}
    </div>
  );
  return (
    <div style={{ marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {(validation.errors || []).map((text) => row(text, '#FF2242'))}
      {(validation.warnings || []).map((text) => row(text, '#e0a32e'))}
    </div>
  );
};

const StepsEditor = ({ steps = [], tools, onChange }) => {
  const update = (index, patch) => onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  const toolOptions = tools.map((tool) => ({ value: tool.name, label: tool.label || tool.name }));
  return (
    <div className="su-af-list">
      {steps.map((step, index) => (
        <div key={index} className="su-ag-step">
          <span className="su-ag-num">{index + 1}</span>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <TextArea rows={1} value={step.text} onChange={(value) => update(index, { text: value })} placeholder="Qué debe conseguir en este paso" />
            <SelectInput value={step.tool || ''} onChange={(value) => update(index, { tool: value || undefined })} options={toolOptions} placeholder="Sin herramienta" />
          </div>
          <RemoveButton onClick={() => onChange(steps.filter((_, i) => i !== index))} />
        </div>
      ))}
      <AddLink onClick={() => onChange([...steps, { text: '' }])}>Agregar paso</AddLink>
    </div>
  );
};

const ContextEditor = ({ items = [], onChange }) => {
  const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  return (
    <div className="su-af-list">
      {items.map((item, index) => (
        <div key={index} className="su-ag-step" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextInput value={item.label} onChange={(value) => update(index, { label: value })} placeholder="Nombre del dato" />
            </div>
            <RemoveButton onClick={() => onChange(items.filter((_, i) => i !== index))} />
          </div>
          <TextInput value={item.value} onChange={(value) => update(index, { value })} placeholder="{{variable}}" mono />
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <InlineSwitch
              checked={item.mode === 'presence'}
              onChange={(on) => update(index, { mode: on ? 'presence' : undefined })}
              label="Solo si existe"
              title="La IA solo sabe si el dato está registrado, no su valor"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextInput value={item.empty} onChange={(value) => update(index, { empty: value })} placeholder="Texto si está vacío" />
            </div>
          </div>
        </div>
      ))}
      <AddLink onClick={() => onChange([...items, { label: '', value: '' }])}>Agregar dato de contexto</AddLink>
    </div>
  );
};

const objectToPairs = (object) => Object.entries(object || {}).map(([key, value]) => ({ key, value }));
const pairsToObject = (pairs) => Object.fromEntries((pairs || []).filter((pair) => pair.key).map((pair) => [pair.key, pair.value]));

const countLabel = (list, one, many) => {
  const total = (list || []).filter((item) => (typeof item === 'string' ? item.trim() : item?.text?.trim())).length;
  if (!total) return 'Sin definir';
  return `${total} ${total === 1 ? one : many}`;
};

export const InstructionsTab = ({ config, setInstructions }) => {
  const ins = config.instructions || {};
  const [open, setOpen] = useState('role');
  const sectionProps = { active: open, setActive: setOpen };
  return (
    <div className="su-ag-acc">
      <ConfigSection id="role" {...sectionProps} icon={LuUser} color="#7563D8" title="Rol y personalidad" summary={String(ins.role || '').trim() || 'Sin definir'}>
        <Field label="Eres…" hint="Usa {{marca}} para el nombre de la empresa.">
          <TextArea rows={2} value={ins.role} onChange={(value) => setInstructions({ role: value })} placeholder="el asistente de WhatsApp de {{marca}}…" />
        </Field>
        <Field label="Personalidad">
          <TextArea rows={2} value={ins.personality} onChange={(value) => setInstructions({ personality: value })} />
        </Field>
        <Field label="Estilo">
          <StringListEditor items={ins.style} onChange={(style) => setInstructions({ style })} icon={LuPalette} iconColor="#7563D8" addLabel="Agregar regla de estilo" />
        </Field>
      </ConfigSection>
      <ConfigSection id="steps" {...sectionProps} icon={LuListOrdered} color="#0a7ba5" title="Qué debe lograr, en orden" summary={countLabel(ins.steps, 'paso', 'pasos')}>
        <StepsEditor steps={ins.steps} tools={config.tools || []} onChange={(steps) => setInstructions({ steps })} />
      </ConfigSection>
      <ConfigSection id="rules" {...sectionProps} icon={LuBan} color="#FF2242" title="Lo que nunca debe hacer" summary={countLabel(ins.rules, 'regla', 'reglas')}>
        <StringListEditor items={ins.rules} onChange={(rules) => setInstructions({ rules })} icon={LuBan} iconColor="#FF2242" addLabel="Agregar regla" />
      </ConfigSection>
      <ConfigSection id="info" {...sectionProps} icon={LuInfo} color="#2fae4e" title="Información útil" summary={countLabel(ins.info, 'dato', 'datos')}>
        <StringListEditor items={ins.info} onChange={(info) => setInstructions({ info })} icon={LuInfo} iconColor="#2fae4e" addLabel="Agregar dato" />
      </ConfigSection>
      <ConfigSection id="examples" {...sectionProps} icon={LuQuote} color="#e0a32e" title="Ejemplos de respuesta" summary={countLabel(ins.examples, 'ejemplo', 'ejemplos')}>
        <StringListEditor items={ins.examples} onChange={(examples) => setInstructions({ examples })} icon={LuQuote} iconColor="#e0a32e" addLabel="Agregar ejemplo" />
        <div className="su-af-hint">Enseñan el tono con el que debe escribir.</div>
      </ConfigSection>
    </div>
  );
};

const parseVar = (value) => {
  const match = String(value || '').match(/^{{([^.{}]+).([^{}]+)}}$/);
  return match ? { node: match[1], path: match[2] } : { node: '', path: '' };
};
const makeVar = (node, path) => (node && String(path || '').trim() ? `{{${node}.${String(path).trim()}}}` : undefined);
const DEFAULT_GEOCODE_PATH = 'features.0.place_name';
const ALL_SETTINGS = ['general', 'context', 'inputs', 'tracking'];

export const SettingsTab = ({ config, set, nodes = [], parts = ALL_SETTINGS }) => {
  const show = (part) => parts.includes(part);
  const tracking = config.tracking;
  const setTracking = (patch) => set({ tracking: { ...(tracking || {}), ...patch } });
  const inputs = config.inputs || {};
  const location = inputs.location || {};
  const setLocation = (patch) => set({ inputs: { ...inputs, location: { ...location, ...patch } } });
  const geocodeOptions = nodes
    .filter((node) => node.type === 'queryApi')
    .map((node) => ({ value: node.id, label: node.data?.title || node.id }));
  const geocodePath = String(location.geocodeAddress || '').match(/^{{[^.{}]+.([^{}]+)}}$/)?.[1] || '';
  const webhookOptions = nodes
    .filter((node) => node.type === 'webHook')
    .map((node) => ({ value: node.id, label: node.data?.title || node.id }));
  const webhookId = parseVar(tracking?.idVar).node || parseVar(tracking?.statusVar).node;
  const chooseWebhook = (nodeId) =>
    setTracking({
      idVar: makeVar(nodeId, parseVar(tracking?.idVar).path),
      statusVar: makeVar(nodeId, parseVar(tracking?.statusVar).path),
      details: (tracking?.details || []).map((detail) => ({ ...detail, value: makeVar(nodeId, parseVar(detail.value).path) || detail.value })),
    });
  const chooseGeocodeNode = (nodeId) => {
    if (!nodeId) {
      setLocation({ geocodeNodeId: undefined, geocodeAddress: undefined });
      return;
    }
    setLocation({ geocodeNodeId: nodeId, geocodeAddress: `{{${nodeId}.${geocodePath || DEFAULT_GEOCODE_PATH}}}` });
  };

  return (
    <>
      {show('general') && (
      <FormSection title="General" style={{ marginBottom: '14px' }}>
        <ToggleField label="Usar Agente IA en este bot" hint="Los mensajes del cliente los atiende la IA. Los eventos de webhook siguen por el flujo." checked={config.enabled} onChange={(enabled) => set({ enabled })} />
        <div style={{ height: '12px' }} />
        <Field label="Nombre de la marca" hint="Así se presenta la IA. Si está vacío usa el nombre de la cuenta.">
          <TextInput value={config.companyName} onChange={(value) => set({ companyName: value })} />
        </Field>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Field label="Modelo" style={{ flex: 1 }}>
            <SelectInput value={config.model || 'gpt-4.1-mini'} onChange={(value) => set({ model: value })} options={MODELS} />
          </Field>
          <Field label="Memoria (min)" style={{ width: '110px' }}>
            <TextInput value={config.memoryTtlMin} onChange={(value) => set({ memoryTtlMin: Number(value) || undefined })} placeholder="120" />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Field label="Tope mensual (turnos)" hint="0 = sin tope" style={{ width: '150px', marginBottom: 0 }}>
            <TextInput value={config.limits?.monthlyTurns} onChange={(value) => set({ limits: { ...(config.limits || {}), monthlyTurns: Number(value) || 0 } })} placeholder="0" />
          </Field>
          <Field label="Mensaje al llegar al tope" style={{ flex: 1, marginBottom: 0 }}>
            <TextInput value={config.limits?.message} onChange={(value) => set({ limits: { ...(config.limits || {}), message: value } })} placeholder="En este momento no podemos atenderte…" />
          </Field>
        </div>
      </FormSection>
      )}

      {show('context') && (
      <FormSection title="Contexto · lo que ve la IA en cada mensaje" style={{ marginBottom: '14px' }}>
        <ContextEditor items={config.context} onChange={(context) => set({ context })} />
      </FormSection>
      )}

      {show('inputs') && (
      <FormSection title="Entradas" style={{ marginBottom: '14px' }}>
        <Field label="Al recibir una ubicación, guardar en" hint="Campos del contacto. Vacío = no se guarda.">
          <div style={{ display: 'flex', gap: '6px' }}>
            <TextInput value={location.latitude} onChange={(value) => setLocation({ latitude: value })} placeholder="latitude" mono />
            <TextInput value={location.longitude} onChange={(value) => setLocation({ longitude: value })} placeholder="longitude" mono />
            <TextInput value={location.address} onChange={(value) => setLocation({ address: value })} placeholder="address" mono />
          </div>
        </Field>
        <Field label="Nodo que convierte la ubicación en dirección" hint="Una Consulta API del lienzo, como Mapbox. Se ejecuta al recibir la ubicación. Sin nodo, se guardan solo las coordenadas.">
          <SelectInput value={location.geocodeNodeId} onChange={chooseGeocodeNode} options={geocodeOptions} placeholder="Ninguno" />
        </Field>
        {location.geocodeNodeId && (
          <Field label="Campo de la respuesta con la dirección" hint="Dónde viene la dirección en la respuesta de ese nodo. En Mapbox es features.0.place_name.">
            <FieldPicker node={nodes.find((node) => node.id === location.geocodeNodeId)} value={geocodePath} onChange={(value) => setLocation({ geocodeAddress: makeVar(location.geocodeNodeId, value) })} />
          </Field>
        )}
        <ToggleField
          label="Entender notas de voz"
          hint="La IA transcribe los audios del cliente y responde como si los hubiera escrito."
          checked={inputs.audio?.transcribe !== false}
          onChange={(on) => set({ inputs: { ...inputs, audio: { ...(inputs.audio || {}), transcribe: on } } })}
        />
        {inputs.audio?.transcribe !== false && (
          <Field label="Duración máxima del audio" hint="Los audios más largos se rechazan amablemente para controlar el costo." style={{ marginTop: '12px' }}>
            <SelectInput
              value={String(inputs.audio?.maxSeconds || 120)}
              onChange={(value) => set({ inputs: { ...inputs, audio: { ...(inputs.audio || {}), maxSeconds: Number(value) } } })}
              options={[
                { value: '30', label: '30 segundos' },
                { value: '60', label: '1 minuto' },
                { value: '120', label: '2 minutos' },
                { value: '300', label: '5 minutos' },
              ]}
            />
          </Field>
        )}
        <div style={{ height: '12px' }} />
        <Field label="Al recibir un contacto, guardar el teléfono en">
          <TextInput value={inputs.contact?.field} onChange={(value) => set({ inputs: { ...inputs, contact: value ? { field: value } : undefined } })} placeholder="phone" mono />
        </Field>
        <Field label="Limpiar al empezar una conversación nueva" hint="Campos separados por coma. No se limpian si hay una operación en curso." style={{ marginBottom: 0 }}>
          <TextInput
            value={(config.resetOnNewConversation?.fields || []).join(', ')}
            onChange={(value) => {
              const fields = csvToList(value);
              set({ resetOnNewConversation: fields.length ? { fields, unlessActive: true } : null });
            }}
            placeholder="latitude, longitude, address"
            mono
          />
        </Field>
      </FormSection>
      )}

      {show('tracking') && (
      <FormSection title="Seguimiento · pedidos, citas, servicios">
        <ToggleField
          label="Hacer seguimiento de una operación"
          hint="La IA sabe si hay algo en curso y su estado, con los eventos del webhook."
          checked={Boolean(tracking)}
          onChange={(on) => set({ tracking: on ? { label: 'Servicio', idField: 'request_id', activeStatuses: [], statusLabels: {}, details: [], staleMinutes: 180 } : null })}
          last={!tracking}
        />
        {tracking && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Field label="Se llama" style={{ flex: 1 }}>
                <TextInput value={tracking.label} onChange={(value) => setTracking({ label: value })} placeholder="Servicio" />
              </Field>
              <Field label="Campo del id en el resultado" style={{ flex: 1 }}>
                <TextInput value={tracking.idField} onChange={(value) => setTracking({ idField: value })} placeholder="request_id" mono />
              </Field>
            </div>
            <Field label="Nodo WebHook que recibe los eventos" hint="Está en la pestaña Eventos del WebHook. De ahí salen el id y el estado.">
              <SelectInput value={webhookId} onChange={chooseWebhook} options={webhookOptions} placeholder="Elegir nodo" />
            </Field>
            {webhookId && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Field label="Campo con el id" style={{ flex: 1 }}>
                  <FieldPicker node={nodes.find((node) => node.id === webhookId)} value={parseVar(tracking.idVar).path} onChange={(value) => setTracking({ idVar: makeVar(webhookId, value) })} />
                </Field>
                <Field label="Campo con el estado" style={{ flex: 1 }}>
                  <FieldPicker node={nodes.find((node) => node.id === webhookId)} value={parseVar(tracking.statusVar).path} onChange={(value) => setTracking({ statusVar: makeVar(webhookId, value) })} />
                </Field>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Field label="Estado al crearse" style={{ flex: 1 }}>
                <TextInput value={tracking.initialStatus} onChange={(value) => setTracking({ initialStatus: value })} placeholder="NEW" mono />
              </Field>
              <Field label="Vence sin novedades (min)" style={{ width: '150px' }}>
                <TextInput value={tracking.staleMinutes} onChange={(value) => setTracking({ staleMinutes: Number(value) || undefined })} placeholder="180" />
              </Field>
            </div>
            <Field label="Estados que significan «en curso»">
              <TextInput value={(tracking.activeStatuses || []).join(', ')} onChange={(value) => setTracking({ activeStatuses: csvToList(value) })} placeholder="NEW, ACCEPTED, ARRIVED" mono />
            </Field>
            <Field label="Cómo se explica cada estado">
              <PairListEditor pairs={objectToPairs(tracking.statusLabels)} onChange={(pairs) => setTracking({ statusLabels: pairsToObject(pairs) })} keyPlaceholder="ACCEPTED" valuePlaceholder="conductor asignado" addLabel="Agregar estado" />
            </Field>
            <Field label="Detalles que ve la IA" style={{ marginBottom: 0 }}>
              <div className="su-af-list">
                {(tracking.details || []).map((detail, index) => {
                  const update = (patch) => setTracking({ details: tracking.details.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '38%' }}>
                        <TextInput value={detail.label} onChange={(value) => update({ label: value })} placeholder="Conductor" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <FieldPicker node={nodes.find((node) => node.id === webhookId)} value={parseVar(detail.value).path || detail.value} onChange={(value) => update({ value: makeVar(webhookId, value) || '' })} />
                      </div>
                      <RemoveButton onClick={() => setTracking({ details: tracking.details.filter((_, i) => i !== index) })} />
                    </div>
                  );
                })}
                <AddLink onClick={() => setTracking({ details: [...(tracking.details || []), { label: '', value: '' }] })}>Agregar detalle</AddLink>
              </div>
            </Field>
          </div>
        )}
      </FormSection>
      )}
    </>
  );
};

const AgentPanel = ({
  config,
  onChange,
  selection,
  setSelection,
  onSave,
  onClose,
  saving,
  dirty,
  validation,
  flows,
  listAreas,
  listAdvisers,
  listTags,
  listStages,
  listLists,
  isDark,
  isExpanded,
  setIsExpanded,
  offsetRight = 16,
}) => {
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const set = (patch) => onChange({ ...config, ...patch });
  const setInstructions = (patch) => set({ instructions: { ...(config.instructions || {}), ...patch } });
  const tools = config.tools || [];
  const toolIndex = selection?.view === 'tool' ? selection.toolIndex : null;
  const tool = toolIndex !== null ? tools[toolIndex] : null;
  const tab = selection?.tab === 'settings' ? 'settings' : 'instructions';

  const removeTool = () => {
    const removed = tools[toolIndex];
    const steps = (config.instructions?.steps || []).map((step) => (step.tool === removed?.name ? { ...step, tool: undefined } : step));
    onChange({ ...config, tools: tools.filter((_, i) => i !== toolIndex), instructions: { ...(config.instructions || {}), steps } });
    setSelection({ view: 'agent', tab: 'instructions' });
  };

  const meta = tool ? TOOL_TYPES[tool.type] || TOOL_TYPES.node : null;

  const toolValidation = useMemo(() => {
    if (!tool || !validation) return validation;
    const label = tool.label || tool.name;
    const mine = (list) => (list || []).filter((text) => text.includes(label) || text.includes(tool.name));
    return { errors: mine(validation.errors), warnings: mine(validation.warnings) };
  }, [tool, validation]);

  return (
    <div
      className="card"
      style={{
        width: isExpanded ? '720px' : '380px',
        maxHeight: 'calc(100vh - 32px)',
        height: 'calc(100vh - 32px)',
        zIndex: 500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: theme.panelBg,
        border: `1px solid ${theme.border}`,
        borderRadius: '14px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.28)',
        position: 'fixed',
        right: `${offsetRight}px`,
        top: '16px',
        transition: 'width 0.3s ease, right 0.2s ease',
      }}
    >
      <NodeEditorChrome
        icon={meta ? meta.icon : LuSparkles}
        color={meta ? meta.color : AGENT_COLOR}
        typeName={meta ? `Herramienta · ${meta.short}` : `Agente IA${dirty ? ' · sin guardar' : ''}`}
        title={tool ? tool.label || tool.name : config.companyName ? `Asistente de ${config.companyName}` : 'Asistente'}
        onRemove={removeTool}
        canRemove={Boolean(tool)}
        onClose={onClose}
        formId="agent-panel-form"
        saving={saving}
        saveDisabled={saving}
        isDark={isDark}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        getIsDirty={() => dirty}
        toast={null}
      >
        <form
          id="agent-panel-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave();
          }}
        >
          {tool ? (
            <>
              <button
                type="button"
                onClick={() => setSelection({ view: 'agent', tab: 'instructions' })}
                style={{ border: 'none', background: 'transparent', color: theme.textSecondary, padding: 0, marginBottom: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
              >
                <LuChevronLeft style={{ fontSize: '14px' }} />
                Agente
              </button>
              <ValidationBox validation={toolValidation} />
              <ToolEditor
                tool={tool}
                index={toolIndex}
                tools={tools}
                flows={flows}
                listAreas={listAreas}
                listAdvisers={listAdvisers}
                listTags={listTags}
                listStages={listStages}
                listLists={listLists}
                hasTracking={Boolean(config.tracking)}
                trackingLabel={(config.tracking?.label || 'operación').toLowerCase()}
                onChange={(next) => set({ tools: tools.map((item, i) => (i === toolIndex ? next : item)) })}
              />
            </>
          ) : (
            <>
              <div style={{ marginBottom: '14px' }}>
                <SegmentedTabs
                  value={tab}
                  onChange={(value) => setSelection({ view: 'agent', tab: value })}
                  options={[
                    { value: 'instructions', label: 'Instrucciones' },
                    { value: 'settings', label: 'Ajustes' },
                  ]}
                />
              </div>
              <ValidationBox validation={validation} />
              {tab === 'instructions' && <InstructionsTab config={config} setInstructions={setInstructions} />}
              {tab === 'settings' && <SettingsTab config={config} set={set} flows={flows} />}
            </>
          )}
        </form>
      </NodeEditorChrome>
    </div>
  );
};

export default AgentPanel;
